<script>
/**
 * Seletor do ESCOPO da conversa: sobre o escritório inteiro, ou sobre UM cliente.
 *
 * Chip no composer, ao lado do seletor de agente — um diz quem responde, o
 * outro sobre quem. Escolhido um cliente, o `chat-cliente-escopo` mostra o
 * cartão dele acima das mensagens.
 *
 * Componente burro: recebe a lista já filtrada e não lê store nem `$access`,
 * para seguir montável sem sessão no teste (mesma disciplina de `agent-select`
 * e `chat-sidebar`).
 */
import { filterClients } from '@modules/navegacao-v2/components/client-switcher-helpers'
import AgentBadge, { ESTADO_SUCESSO, ESTADO_VAZIO } from '@modules/aawz-intelligence-v2/components/agent-badge/agent-badge.vue'
import { ESCOPO_CLIENTE_TEXT } from '@modules/aawz-intelligence-v2/constants/Text.escopo-cliente'

const ESCOPO_GERAL = 'Escopo: Geral'
const TAMANHO_ICONE = 12
// Frames de espera pelo painel do `v-menu` ficar VISÍVEL. A Vuetify monta o
// conteúdo fora desta árvore e só liga o `v-show` dele depois de dois
// `requestAnimationFrame` aninhados (`VMenu.activate` → `startTransition`), e
// numa máquina carregada isso escorrega. O teto existe para um painel que não
// abriu não deixar um laço vivo.
const TENTATIVAS_DE_FOCO = 10

// Lista longa não cabe no menu; a busca é o caminho para o resto.
const TETO_VISIVEL = 8

export default {
  name: 'EscopoSelect',
  components: { AgentBadge },
  props: {
    // spec-009 [T008] — `agenteAtualDaConversa`; `null` ⇒ sem badge (AC-4.5).
    agente: {
      type: Object,
      default: null,
    },
    // Só o ícone, sem rótulo e sem o X — o composer do modo limpo.
    //
    // O X não faz falta: "Todo o escritório", a primeira opção do painel, já
    // chama o mesmo `limpar()`. A pílula com X era um segundo caminho para uma
    // ação que o menu já oferece.
    soIcone: {
      type: Boolean,
      default: false,
    },
    clientes: {
      type: Array,
      default: () => [],
    },
    // `null` = conversa geral do escritório.
    cliente: {
      type: Object,
      default: null,
    },
    // ★ Conversa com histórico: o cliente NÃO pode mais mudar. O backend
    // vincula o cliente na CRIAÇÃO e não re-vincula quem retoma
    // (`usecase_ia_crm_chat`, docstring de `id_cliente_app`), então trocar aqui
    // seria ignorado em silêncio — o mesmo motivo pelo qual o agente e o modelo
    // já travam. Chip clicável que não faz nada é pior que chip apagado.
    // Na `/ia` o chip não abre painel: emite para o composer, que abre o palette
    // com a lista de clientes. Opt-in — as demais superfícies não mudam.
    abrirPorFora: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return { aberto: false, busca: '', TAMANHO_ICONE }
  },
  computed: {
    // No modo só-ícone o nome do cliente sai da tela; o tooltip é quem o
    // devolve, e ele é o único lugar em que o escopo ativo se anuncia.
    //
    // [G-3, design.json it.3, SHOULD] Travado (`disabled`), o chip esmaecia
    // sem dizer POR QUÊ — o tooltip seguia igual ao destravado. Acrescenta
    // o motivo do vínculo (`ESCOPO_CLIENTE_TEXT`, texto único também usado
    // em `chat-cliente-escopo.vue`).
    tooltipDaCaixa() {
      const base = 'Cliente em foco: ' + this.rotulo
      if (!this.disabled) return base
      return base + '. ' + ESCOPO_CLIENTE_TEXT.motivoTravamento
    },
    rotulo() {
      if (!this.cliente) return ESCOPO_GERAL
      return this.cliente.name || this.cliente.nome || ESCOPO_GERAL
    },
    // Reusa o filtro do `client-switcher` (nome ou id) em vez de escrever um
    // segundo — duas buscas de cliente divergiriam na primeira mudança.
    resultados() {
      return filterClients(this.clientes || [], this.busca).slice(
        0,
        TETO_VISIVEL
      )
    },
    temResultado() {
      return this.resultados.length > 0
    },
    /**
     * `--primary` quando há cliente em foco; cinza quando não há.
     *
     * ⚠️ Só no modo SÓ-ÍCONE. No composer de três chips o ícone é sempre
     * `--primary`, e isso continua valendo: lá o preenchimento é neutro (a
     * plataforma é white-label) e é o ÍCONE que carrega a cor dos três — um
     * cinza no meio quebrava o trio. Medido em 4.35 sobre branco, acima do
     * mínimo de 3:1 da WCAG 1.4.11 para elemento não-textual.
     *
     * Sem rótulo, porém, o trio deixou de existir e a cor ficou sendo a única
     * pista do estado: primary com e sem cliente dizia a mesma coisa nas duas
     * situações. Cinza em repouso devolve ao ícone a função de anunciar que há
     * alguém em foco.
     */
    corDoIcone() {
      if (this.soIcone && !this.cliente) return 'var(--neutral-700)'
      return 'var(--primary)'
    },
    // spec-009 [T008] — sem hub, sem badge (AC-4.5); com hub sem agentSlug, estado `vazio`.
    mostraAgenteBadge() {
      return Boolean(this.agente && this.agente.hub && !this.soIcone)
    },
    estadoDoAgenteBadge() {
      return this.agente && this.agente.agentSlug ? ESTADO_SUCESSO : ESTADO_VAZIO
    },
    // Classe única por instância, para o `opcoesDoPainel` achar ESTE painel entre
    // os que a Vuetify deixa desanexados no DOM. Vai junto da classe de estilo
    // (`escopo-select__menu`), que continua sendo o gancho do
    // `design/_escopo-select-menu.sass`.
    classeDoMenu() {
      return `escopo-select__menu--${this._uid}`
    },
  },
  watch: {
    /**
     * Ao ABRIR, o foco vai para a primeira opção da lista.
     *
     * ⚠️ Não dá para fazer isso num `$nextTick` só: o `v-menu` monta o conteúdo
     * fora da árvore deste componente e nem sempre na mesma tick. Daí a espera
     * por FRAME, com um teto — sem o teto, um painel que não abrisse deixaria
     * um laço vivo.
     */
    aberto(estaAberto) {
      // O `keydown` do documento só enquanto o painel está aberto — mesmo
      // liga/desliga de `composer-inline-select.vue`; um listener pela vida
      // inteira do componente rodava em cada tecla de cada composer vivo.
      if (!estaAberto) {
        document.removeEventListener('keydown', this.aoTeclar)
        return
      }
      document.addEventListener('keydown', this.aoTeclar)
      this.focarPrimeiraOpcao(TENTATIVAS_DE_FOCO)
    },
  },
  beforeDestroy() {
    // Cinto e suspensório: destruído com o painel aberto, o watcher já não roda.
    document.removeEventListener('keydown', this.aoTeclar)
  },
  methods: {
    // Abre de fora — o composer chama pelo comando `/cliente`. O `watch: aberto`
    // cuida de levar o foco para a primeira opção, igual à abertura por clique.
    abrir() {
      if (this.disabled) return
      // Sem painel local neste modo: quem abre a lista é o palette.
      if (this.abrirPorFora) {
        this.$emit('abrir-externo')
        return
      }
      this.busca = ''
      this.aberto = true
    },
    // Lido pelo `algumMenuAberto` do composer.
    isOpen() {
      return this.aberto
    },
    // `disabled` vence: conversa com histórico não troca de escopo, nem pelo
    // dropdown nem pelo palette.
    aoClicarNoGatilho() {
      if (!this.abrirPorFora || this.disabled) return
      this.$emit('abrir-externo')
    },
    focarPrimeiraOpcao(tentativasRestantes) {
      const opcoes = this.opcoesDoPainel()
      // ⚠️ `offsetParent` nulo = painel ainda em `display:none`, e `.focus()` em
      // nó oculto é no-op SILENCIOSO. Sem esta guarda a tentativa era dada como
      // boa assim que os botões existiam no DOM — antes de a Vuetify ligar o
      // `v-show` do conteúdo — e o foco simplesmente nunca entrava na lista.
      if (opcoes.length && opcoes[0].offsetParent) {
        opcoes[0].focus()
        return
      }
      if (tentativasRestantes > 0) {
        window.requestAnimationFrame(() =>
          this.focarPrimeiraOpcao(tentativasRestantes - 1)
        )
      }
    },
    // Um lugar só para achar as opções — o painel é teleportado pelo `v-menu`
    // para fora deste componente, então `$refs` e `$el` não o alcançam.
    //
    // ⚠️ Busca pela classe da INSTÂNCIA, não por `.escopo-select__painel` solto:
    // a Vuetify deixa o conteúdo desanexado no DOM depois de fechar (`v-show`,
    // não `v-if`), então com dois composers vivos (pop-up + rota, `agent-chat`)
    // um seletor genérico devolveria o painel do outro — escondido. Mesmo padrão
    // do `_base-actions-menu.vue`.
    opcoesDoPainel() {
      const painel = document.querySelector(
        `.${this.classeDoMenu} .escopo-select__painel`
      )
      if (!painel) return []
      return [].slice.call(painel.querySelectorAll('.escopo-select__opcao'))
    },
    /**
     * Setas e Esc enquanto o painel está aberto — ouvidos no DOCUMENTO.
     *
     * ⚠️ O `v-menu` teleporta o conteúdo para fora deste componente e move o
     * foco para dentro dele, então nem um handler no gatilho nem um no painel
     * pegam os dois casos. No documento pega sempre.
     */
    aoTeclar(evento) {
      if (!this.aberto) return

      if (evento.key === 'ArrowDown') {
        evento.preventDefault()
        this.moverFoco(1)
      } else if (evento.key === 'ArrowUp') {
        evento.preventDefault()
        this.moverFoco(-1)
      } else if (evento.key === 'Escape') {
        this.aberto = false
      }
    },
    escolher(cliente) {
      this.aberto = false
      this.busca = ''
      this.$emit('change', cliente)
    },
    limpar() {
      this.escolher(null)
    },
    /**
     * Anda pelas opções com as setas.
     *
     * Do campo de busca (onde o foco nasce), ↓ entra na lista pelo primeiro
     * item e ↑ pelo último — é o que o usuário espera de um combo. Dentro da
     * lista a navegação circula, para não parar num beco no fim.
     *
     * ⚠️ Lê o DOM em vez de `$refs`. O "Todo o escritório" está solto e os
     * clientes vêm de um `v-for`; no Vue 2, o mesmo `ref` nos dois casos
     * colide — um vira elemento e o outro array, e a lista sai incompleta.
     */
    moverFoco(passo) {
      const opcoes = this.opcoesDoPainel()
      if (!opcoes.length) return

      const atual = opcoes.indexOf(document.activeElement)
      if (atual === -1) {
        opcoes[passo > 0 ? 0 : opcoes.length - 1].focus()
        return
      }
      opcoes[(atual + passo + opcoes.length) % opcoes.length].focus()
    },
  },
}
</script>

<template>
  <v-menu
    v-model="aberto"
    :close-on-content-click="false"
    offset-y
    top
    :content-class="`escopo-select__menu ${classeDoMenu}`"
  >
    <template v-slot:activator="{ on, attrs }">
      <!-- A pílula é o INVÓLUCRO; abrir e remover são dois botões IRMÃOS dentro
           dela. O X já esteve dentro do ativador e o `.stop` não segurava — o
           menu abria no mesmo gesto que tirava o cliente. E interativo dentro
           de `<button>` é HTML inválido que o Vue não acusa. -->
      <!-- ⚠️ Sem `@keydown` de seta aqui: as setas são ouvidas SÓ no `document`
           (`aoTeclar`). Ligadas nas duas vias, o mesmo evento era tratado duas
           vezes — uma no template e outra ao borbulhar até o documento — e o
           foco andava de dois em dois itens. Mesma correção que o
           `composer-inline-select` já tinha feito. -->
      <span
        class="escopo-select__caixa ia-texto-auxiliar"
        :class="{
          'escopo-select__caixa--so-icone': soIcone,
          'escopo-select__caixa--ativo': cliente,
          'escopo-select__caixa--travado': disabled,
        }"
      >
        <!-- ⚠️ Com `abrirPorFora` o gatilho NÃO recebe o `on` do `v-menu`: é o
             que impede o painel local de abrir junto do palette. Tirar só o
             listener não bastaria — o `v-menu` abre pelo próprio ativador. -->
        <button
          v-bind="attrs"
          type="button"
          class="escopo-select__abrir"
          :disabled="disabled"
          :aria-label="tooltipDaCaixa"
          :title="tooltipDaCaixa"
          v-on="abrirPorFora ? {} : on"
          @click="aoClicarNoGatilho"
        >
          <icon-svg name="user-2" :size="TAMANHO_ICONE" :color="corDoIcone" />
          <span v-if="!soIcone" class="text-truncate">{{ rotulo }}</span>
          <icon-svg
            v-if="!soIcone && !cliente"
            name="arrow-down-light"
            :size="TAMANHO_ICONE"
            color="var(--neutral-600)"
          />
        </button>

        <!-- spec-009 [T008] — quem responde, ao lado do cliente em foco. Sem
             CSS próprio: o `gap: 4px` da caixa já dá o respiro. -->
        <AgentBadge
          v-if="mostraAgenteBadge"
          :hub="agente.hub"
          :agent-slug="agente.agentSlug || ''"
          :motivo="agente.motivo || ''"
          :estado="estadoDoAgenteBadge"
        />

        <button
          v-if="cliente && !soIcone"
          type="button"
          class="escopo-select__remover"
          :disabled="disabled"
          title="Remover o cliente do escopo"
          aria-label="Remover o cliente do escopo"
          @click="limpar"
        >
          <icon-svg
            name="close-circle"
            :size="TAMANHO_ICONE"
            :color="corDoIcone"
          />
        </button>
      </span>
    </template>

    <!-- Navegação por teclado no painel inteiro, busca inclusive: ↓/↑ andam
         pelas opções, Enter escolhe (comportamento nativo do <button>) e Esc
         fecha. Sem isto só o Tab funcionava, e percorrer 20 clientes de Tab em
         Tab não é navegar. ↓/↑ e Esc vivem no `aoTeclar`, ouvido no `document`:
         o `v-menu` teleporta este conteúdo para fora do gatilho, e um handler
         só aqui não pegaria o caso de o foco ainda estar no gatilho. -->
    <!-- `tabindex="-1"` para o painel poder receber foco por script. -->
    <div ref="painel" tabindex="-1" class="escopo-select__painel">
      <input-by-type
        v-model="busca"
        class="escopo-select__busca"
        type="search"
        max-width="100%"
        placeholder="Buscar cliente"
        aria-label="Buscar cliente"
      />

      <button
        type="button"
        class="ia-sidebar-row escopo-select__opcao"
        @click="limpar"
      >
        Todo o escritório
      </button>

      <button
        v-for="item in resultados"
        :key="item.id"
        type="button"
        class="ia-sidebar-row escopo-select__opcao"
        @click="escolher(item)"
      >
        <span class="text-truncate">{{ item.name }}</span>
      </button>

      <p v-if="!temResultado" class="escopo-select__vazio ia-texto-auxiliar">
        Nenhum cliente encontrado.
      </p>
    </div>
  </v-menu>
</template>

<style lang="sass" scoped>
// Mesma receita do chip de agente e do de modelo: os três dizem uma escolha que
// vale para a próxima mensagem e precisam ler como um trio.
//
// ⚠️ A borda TRACEJADA do escopo geral saiu: na mesma fileira que dois chips
// neutros, ela fazia este ler como controle de outra família. O que ficou é o
// preenchimento tingido quando há cliente em foco (ver `--ativo`) — aquele é
// estado, não estilo de família, e é a única diferença que os três precisam ter.
//
// ⚠️ Sem `height` fixo: a altura sai do padding + font-size, como nos vizinhos.
// Ele já veio com 32px do protótipo enquanto os outros dois tinham 21.
.escopo-select__caixa
  color: var(--neutral-900)
  // Mesma receita nos três chips do composer — agente, modelo e escopo.
  // Mudou aqui, mude nos outros dois (`grep -rn "chip do composer"`).
  border-radius: 999px
  padding: 4px 10px
  background: var(--neutral-100)

  display: inline-flex
  align-items: center
  gap: 4px
  max-width: 220px

  &:hover
    background: var(--neutral-200)

// Cliente em foco: preenchimento com a cor do ESCRITÓRIO bem diluída — mesma
// receita do CRM (`color-mix` sobre branco), que é white-label por construção.
// Um tint fixo não sobreviveria a escritório com identidade própria.
//
// É a ÚNICA divergência autorizada em relação aos outros dois chips: aqui ela
// carrega ESTADO (a conversa está recortada num cliente), não identidade visual.
// O rótulo virar o nome do cliente ajuda, mas some no truncamento de 220px —
// a cor de fundo é o que se lê de relance.
.escopo-select__caixa--ativo
  background: color-mix(in srgb, var(--primary) 10%, var(--white))
  color: var(--primary)

  &:hover
    background: color-mix(in srgb, var(--primary) 16%, var(--white))

// Só-ícone: círculo de 30px, mesma régua do "+", do agente e do enviar.
.escopo-select__caixa--so-icone
  padding: 0
  // Idem ao agente: nada de cinza em repouso. O `--ativo` (cliente em foco)
  // segue pintando com a cor da marca, que ali é informação, não decoração.
  background: transparent

  .escopo-select__abrir
    // ★ 34px e não os 30 do composer padrão: nesta caixa de 52px os botões
    // ficavam pequenos demais para a altura da linha. Vale para os QUATRO —
    // "+", agente, escopo e enviar — senão a fileira fica desalinhada.
    // Mudou aqui, mude nos outros quatro (`grep -rn "34px e não os 30"`).
    width: 34px
    height: 34px
    padding: 0
    justify-content: center
    border-radius: 50%

.escopo-select__abrir
  display: inline-flex
  align-items: center
  gap: 4px
  min-width: 0
  padding: 1px 4px
  color: inherit
  font: inherit
  background: transparent

  &:focus-visible
    outline: 2px solid var(--primary)
    outline-offset: 2px

.escopo-select__remover
  display: inline-flex
  flex-shrink: 0
  padding: 1px
  background: transparent
  opacity: 0.7

  &:hover
    opacity: 1

  &:focus-visible
    outline: 2px solid var(--primary)
    outline-offset: 2px

// Travado: esmaece como os outros dois chips do composer e some o cursor de
// clique. NÃO usa `opacity` no rótulo do cliente a ponto de reprovar contraste —
// é o mesmo 0.45 do `.send-message-anexo:disabled`, que já vive na mesma barra.
.escopo-select__caixa--travado
  opacity: 0.45

  .escopo-select__abrir,
  .escopo-select__remover
    cursor: not-allowed

.escopo-select__painel
  width: 280px
  // Sem `padding` e sem fundo próprio: quem desenha a superfície é a casca
  // (`design/_escopo-select-menu.sass`), igual ao menu do agente. O fundo
  // quente que estava aqui era o que fazia os dois destoarem lado a lado.
  padding: 0

.escopo-select__busca
  margin-bottom: 4px

.escopo-select__opcao
  width: 100%
  text-align: left

  // Foco lê IGUAL ao hover da `.ia-sidebar-row` — a seta e o mouse apontam a
  // mesma coisa, então mostram a mesma coisa. Mesma decisão do
  // `.composer-inline-select__item`, o menu irmão desta barra.
  //
  // `:focus` e NÃO `:focus-visible`: o foco chega por script
  // (`focarPrimeiraOpcao` ao abrir). Com o menu aberto pelo MOUSE, o Chrome não
  // trata o foco programático como "visível" e o `:focus-visible` não casa — a
  // opção ficava focada sem destaque nenhum.
  &:focus
    background: var(--sidebar-warm-hover)

.escopo-select__vazio
  color: var(--neutral-600)
  margin: 8px 4px 4px
</style>
